/**
 * Settings that outlive a session, kept in localStorage.
 *
 * Only the host app writes most of these; the viewer page reads `appearance`
 * so a friend's browser can follow the same theme choice if they made one.
 * Storage can be blocked (private windows, cleared site data), so every access
 * is wrapped and the app works from the defaults alone.
 */

import type { EncoderChoice } from './tauri'
import { savedSharePreference, type SharePreference } from './share-preference'

export type Language = 'en' | 'pt-BR'

/** The browser's language, narrowed to one we have. */
export function detectLanguage(): Language {
  const preferred = typeof navigator === 'undefined' ? 'en' : navigator.language
  return preferred.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en'
}

export type Appearance = 'system' | 'light' | 'dark'
export type Preset = 'smooth' | 'crisp' | 'light' | 'custom'

export interface Prefs {
  /** Set once the first-run screen has been completed. */
  onboarded: boolean
  displayName: string
  connection: SharePreference
  appearance: Appearance
  preset: Preset
  fps: 30 | 60
  bitrateMbps: number
  viewerCap: number
  showStats: boolean
  /** Unlocks diagnostics (stats, protocol debug). Always on in dev builds; see dev.svelte.ts. */
  devMode: boolean
  miniOnLive: boolean
  /** Show the click-through chat overlay over the game while live. */
  chatOverlay: boolean
  /** Translucent chat over the video while watching. Only affects this viewer. */
  viewerChatOverlay: boolean
  /** Which side of the screen the chat overlays sit on. */
  chatSide: 'left' | 'right'
  /** Global key that opens the host's chat overlay for typing. */
  chatHotkey: string
  encoder: EncoderChoice
  language: Language
}

/** The three named looks from the stream settings sheet. */
export const PRESETS: Record<
  Exclude<Preset, 'custom'>,
  { label: string; fps: 30 | 60; mbps: number }
> = {
  smooth: { label: 'Smooth', fps: 60, mbps: 8 },
  crisp: { label: 'Crisp', fps: 30, mbps: 12 },
  light: { label: 'Light', fps: 30, mbps: 4 },
}

const KEY = 'sofa.prefs.v1'

const DEFAULTS: Prefs = {
  onboarded: false,
  displayName: '',
  connection: 'auto',
  appearance: 'light',
  preset: 'smooth',
  fps: 60,
  bitrateMbps: 8,
  viewerCap: 4,
  showStats: false,
  devMode: false,
  miniOnLive: false,
  chatOverlay: true,
  viewerChatOverlay: true,
  chatSide: 'left',
  chatHotkey: 'Ctrl+Shift+Space',
  encoder: 'auto',
  language: detectLanguage(),
}

function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const saved = JSON.parse(raw) as Partial<Prefs> & { share?: unknown }
      return { ...DEFAULTS, ...saved, connection: savedSharePreference(saved) }
    }
  } catch {
    // Blocked or corrupt storage: fall through to the defaults.
  }
  return { ...DEFAULTS }
}

export const prefs = $state<Prefs>(load())

/** Persist the current values. Call after changing any field. */
export function savePrefs(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify($state.snapshot(prefs)))
  } catch {
    // Not persisting is fine; the values still apply for this session.
  }
}

/** Reflect the appearance choice on <html> so the stylesheet can follow it. */
export function applyAppearance(): void {
  const root = document.documentElement
  root.setAttribute('data-theme', prefs.appearance)
}

const systemDark =
  typeof window === 'undefined' || !window.matchMedia
    ? null
    : window.matchMedia('(prefers-color-scheme: dark)')

/** The theme on screen right now, with 'system' resolved against the OS. */
export function effectiveAppearance(): 'light' | 'dark' {
  if (prefs.appearance !== 'system') return prefs.appearance
  return systemDark?.matches ? 'dark' : 'light'
}

/** Flip between light and dark from whatever is showing, then remember it. */
export function toggleAppearance(): void {
  prefs.appearance = effectiveAppearance() === 'dark' ? 'light' : 'dark'
  applyAppearance()
  savePrefs()
}
